import React from 'react';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { useTranslation } from '../contexts/LanguageContext';

const Footer = () => {
    const { t } = useTranslation();

    return (
        <>
            <footer className="bg-white border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    
                    {/* Main Footer Content */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 mb-8">

                        {/* Brand Section */}
                        <div className="md:col-span-4 lg:col-span-4">
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-lg border border-gray-100">
                                <h3 className="font-bold text-3xl text-blue-900 mb-4 text-center">EasySubs</h3>
                                <div className="text-gray-600 text-center space-y-2">
                                    <p className="font-semibold text-gray-700"></p>
                                    <p>Milan, Italy</p>
                                    <p className="text-sm text-gray-500 mt-4">
                                        {t('footer.description', 'Take control of your subscriptions')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <div className="md:col-span-4 lg:col-span-2 lg:ml-8">
                            <h6 className="text-blue-900 text-lg font-bold mb-6 uppercase tracking-wide">
                                {t('footer.navigation', 'Navigation')}
                            </h6>
                            <ul className="space-y-3">
                                <li>
                                    <HashLink 
                                        to="/#about" 
                                        className="text-gray-600 hover:text-blue-900 font-medium transition-all duration-300 hover:translate-x-1 inline-block"
                                    >
                                        {t('navbar.about', 'About')}
                                    </HashLink>
                                </li>
                                <li>
                                    <HashLink 
                                        to="/#features" 
                                        className="text-gray-600 hover:text-blue-900 font-medium transition-all duration-300 hover:translate-x-1 inline-block"
                                    >
                                        {t('navbar.features', 'Features')}
                                    </HashLink>
                                </li>
                                <li>
                                    <Link 
                                        to="/contact" 
                                        className="text-gray-600 hover:text-blue-900 font-medium transition-all duration-300 hover:translate-x-1 inline-block"
                                    >
                                        {t('navbar.contact', 'Contact')}
                                    </Link>
                                </li>
                                <li>
                                    <Link 
                                        to="/demo" 
                                        className="text-gray-600 hover:text-blue-900 font-medium transition-all duration-300 hover:translate-x-1 inline-block"
                                    >
                                        {t('navbar.getStarted', 'Get Started')}
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        {/* Social Media Section */}
                        <div className="md:col-span-4 lg:col-span-4 lg:ml-8">
                            <h6 className="text-blue-900 text-lg font-bold mb-6 uppercase tracking-wide">
                                {t('footer.connect', 'Connect With Us')}
                            </h6>
                            <p className="text-gray-600 mb-6 font-medium">
                                {t('footer.connectDescription', 'Follow us for updates and tips on managing your subscriptions.')}
                            </p>
                            
                            <div className="flex space-x-4">
                                {/* LinkedIn */}
                                {/* https://www.linkedin.com/company/easysubs */}
                                <a 
                                    href="https://www.linkedin.com/company/easysubs" 
                                    className="flex items-center justify-center w-12 h-12 bg-blue-100 hover:bg-blue-200 text-blue-900 rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl"
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    aria-label="LinkedIn"
                                >
                                    <svg className="w-6 h-6 fill-current" viewBox="0 0 48 48">
                                        <path d="M44.45 0H3.55C1.59 0 0 1.58 0 3.53v40.94C0 46.42 1.59 48 3.55 48h40.9c1.96 0 3.55-1.58 3.55-3.53V3.53C48 1.58 46.41 0 44.45 0zM14.24 40.9H7.12V18.01h7.12V40.9zM10.68 14.96c-2.28 0-4.13-1.85-4.13-4.13 0-2.28 1.85-4.13 4.13-4.13 2.28 0 4.13 1.85 4.13 4.13 0 2.28-1.85 4.13-4.13 4.13zM40.9 40.9h-7.12V29.77c0-2.76-.06-6.31-3.85-6.31-3.85 0-4.43 3-4.43 6.11V40.9h-7.12V18.01h6.84v3.13h.1c.95-1.8 3.27-3.85 6.73-3.85 7.2 0 8.53 4.74 8.53 10.91V40.9z" />
                                    </svg>
                                </a>

                                {/* Facebook */}
                                <Link 
                                    to="#" 
                                    className="flex items-center justify-center w-12 h-12 bg-blue-100 hover:bg-blue-200 text-blue-900 rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl"
                                    aria-label="Facebook"
                                >
                                    <svg className="w-6 h-6 fill-current" viewBox="0 0 32 32">
                                        <path d="M14.023 24L14 17h-3v-3h3v-2c0-2.7 1.672-4 4.08-4 1.153 0 2.144.086 2.433.124v2.821h-1.67c-1.31 0-1.563.623-1.563 1.536V14H21l-1 3h-2.72v7h-3.257z" />
                                    </svg>
                                </Link>

                                {/* Instagram */}
                                <a 
                                    href="https://www.instagram.com/easysubs.app?igsh=MWp5a2MzYWk4bXQ4aw==" 
                                    className="flex items-center justify-center w-12 h-12 bg-blue-100 hover:bg-blue-200 text-blue-900 rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl"
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    aria-label="Instagram"
                                >
                                    <svg className="w-6 h-6 fill-current" viewBox="0 0 16 16">
                                        <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.9 3.9 0 0 0-1.417.923A3.9 3.9 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.9 3.9 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.9 3.9 0 0 0-.923-1.417A3.9 3.9 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599s.453.546.598.92c.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.5 2.5 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.5 2.5 0 0 1-.92-.598 2.5 2.5 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233s.008-2.388.046-3.231c.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92s.546-.453.92-.598c.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92m-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217m0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334"/>
                                    </svg>
                                </a>
                            </div>
                        </div>

                    </div>

                    {/* Bottom Copyright Section */}
                    <div className="border-t border-gray-200 pt-8">
                        <div className="flex flex-col md:flex-row justify-between items-center">
                            <div className="text-gray-600 text-sm font-medium mb-4 md:mb-0">
                                {t('footer.copyright', 'Copyright')} &copy; {new Date().getFullYear()}{" "}
                                <HashLink
                                    to="/#hero"
                                    className="text-blue-900 hover:text-blue-700 font-semibold transition-colors duration-300"
                                >
                                    EasySubs
                                </HashLink>. {t('footer.rightsReserved', 'All rights reserved.')}
                            </div>
                            
                            {/* Additional links */}
                            <div className="flex space-x-6 text-sm">
                                <a 
                                    href="/terms" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="text-gray-500 hover:text-blue-900 transition-colors duration-300"
                                >
                                    {t('footer.terms', 'Terms')}
                                </a>
                                <a 
                                    href="/privacy-policy" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="text-gray-500 hover:text-blue-900 transition-colors duration-300"
                                >
                                    {t('footer.privacy', 'Privacy')}
                                </a>
                            </div>
                        </div>
                    </div>

                </div>
            </footer>
        </>
    )
}

export default Footer;